import theme from '@/common/theme';
import Alert from '@/components/alert/alert-component';
import FileUpload from '@/components/file-upload/file-upload-component';
import { Col, Row } from '@/components/grid/grid-component';
import Progress from '@/components/progress-bar/progress-bar-component';
import { createTemplateExcelWithMultipleSheets } from '@/utils/excel-utils';
import { S3Client } from '@aws-sdk/client-s3';
import { Upload } from '@aws-sdk/lib-storage';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FiDownload } from 'react-icons/fi';
import PermissionWrapper from '@/components/permission-wrapper/permission-wrapper-component';
import {
  PrimaryButton,
  PrimaryOutlineButton,
} from '@/components/button/button-component';
import styled from 'styled-components';

const modalRowStyle = {
  marginLeft: '0px',
  marginRight: '0px',
  marginBottom: '0px',
};

const modalColStyle = {
  paddingLeft: '0px',
  paddingRight: '0px',
};

const downloadTemplateStyledButton = {
  border: 'unset',
  backgroundColor: 'unset',
};

const Body = styled.div`
  width: 100%;
  display: block;
  position: relative;
  margin-bottom: 20px;
  padding: 16px;
`;

const RowStyle = styled(Row)`
  width: 100% !important;
  background-color: #eaedf3 !important;
  margin-bottom: 0px !important;
  padding: 24px 15px 24px 0px !important;
  justify-content: space-between !important;
  margin-right: auto !important;
  margin-left: auto !important;
`;

/**
 * Import customers modal
 * @param {object} root0 - The root object containing props
 * @param {Function} root0.onSubmit - Callback function to submit the form
 * @returns {JSX.Element} - The rendered JSX element
 */
const ImportCustomersModal = ({ onSubmit }) => {
  const [progress, setProgress] = useState(0);
  const [downloadTemplate, setDownloadTemplate] = useState(false);
  const [fileName, setFileName] = useState('');
  const [isDropped, setIsDropped] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const { setValue } = useFormContext();
  const uploadToS3 = async (fileToUpload) => {
    setIsUploading(true);
    const file = fileToUpload[0];
    const target = {
      Bucket: import.meta.env.VITE_BUCKET_NAME,
      Key: `customers-upload/${file.name}`,
      Body: file,
    };

    let parts = {};

    if (file.size >= 1024 * 1024 * 2) {
      parts = {
        queueSize: 4, // optional concurrency configuration
        partSize: 1024 * 1024 * 2, // optional size of each part, in bytes, at least 5MB
      };
    }

    try {
      const parallelUploads3 = new Upload({
        client: new S3Client({
          region: import.meta.env.VITE_REGION ?? 'eu-west-1',
          credentials: {
            accessKeyId: import.meta.env.VITE_BUCKET_ACCESS_KEY,
            secretAccessKey: import.meta.env.VITE_BUCKET_SECRET_KEY,
          },
        }),
        params: target,

        tags: [
          /* ... */
        ], // optional tags
        ...parts,
        leavePartsOnError: false, // optional manually handle dropped parts
      });

      parallelUploads3.on('httpUploadProgress', (prog) => {
        if (prog.total === prog.loaded) {
          setProgress(100);
        } else {
          setProgress(Math.round((prog.loaded / prog.total) * 100));
        }
      });

      await parallelUploads3.done().then(() => {
        setValue('file_name', file.name);
        setIsUploading(false);
      });
    } catch (e) {
      setIsUploading(false);
      Alert({
        icon: 'error',
        text: e.message,
      });
    }
  };

  const onDrop = (acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      Alert({
        icon: 'error',
        text: 'Please upload only XLSX files.',
      });
      setIsDropped(false);
      setFileName('');
    } else {
      acceptedFiles.map((file) => setFileName(file.name));
      uploadToS3(acceptedFiles);

      setIsDropped(true);
    }
  };

  return (
    <Row style={modalRowStyle}>
      <Body>
        {progress === 0 || progress >= 100 || isUploading ? (
          <Col style={modalColStyle} xs={12}>
            <FileUpload
              fileType="xlsx"
              maxNumberFiles={1}
              onDrop={onDrop}
              fileName={fileName}
              isDropped={isDropped}
              onUpload={(file) => uploadToS3(file)}
            />
          </Col>
        ) : (
          <Col style={modalColStyle} xs={12}>
            <Progress
              fileName={fileName}
              progress={progress}
              isUploading={isUploading}
              color={theme.COLOR_PRIMARY}
            />
          </Col>
        )}
      </Body>
      <RowStyle>
        <PrimaryOutlineButton
          buttonWidth="fit-content"
          icon={<FiDownload />}
          disabled={false}
          margin="0 0 0 0"
          type="button"
          loading={downloadTemplate}
          style={downloadTemplateStyledButton}
          title="Download Template"
          onClick={() => {
            setDownloadTemplate(true);

            createTemplateExcelWithMultipleSheets(
              [
                {
                  name: 'customers',
                  data: ['name', 'address', 'contact_number'],
                },
                {
                  name: 'contact_persons',
                  data: [
                    'customer_name',
                    'name',
                    'email',
                    'contact_number',
                    'position',
                    'superior_name',
                    'default_recipient_of_quotes',
                  ],
                },
                {
                  name: 'operating_sites',
                  data: [
                    'customer_name',
                    'name',
                    'country',
                    'nearest_town',
                    'site_commodity',
                    'other',
                  ],
                },
              ],
              'Import customers',
            );

            setDownloadTemplate(false);
          }}
        />
        <PermissionWrapper
          permissions={['customer.create', 'customer.read', 'customer.update']}
          satisfyAll={false}
        >
          <PrimaryButton
            buttonWidth="108px"
            disabled={false}
            margin="0 0 0 0"
            type="button"
            loading={downloadTemplate}
            title="Import"
            onClick={() => {
              onSubmit(fileName);
            }}
          />
        </PermissionWrapper>
      </RowStyle>
    </Row>
  );
};

ImportCustomersModal.propTypes = {
  onSubmit: PropTypes.func.isRequired,
};

export default ImportCustomersModal;
